<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import { useRoute } from 'vue-router';
import { ArrowDownTrayIcon, CheckIcon, ChevronDownIcon, PlusIcon, UserGroupIcon } from '@heroicons/vue/24/outline';
import SavedViewIcon from '@/components/savedViews/SavedViewIcon.vue';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useMountedWhileOpen } from '@/composables/useMountedWhileOpen';
import type { AreaPicker } from '@/navigation/areaPicker';

/**
 * On a phone, the area's sidebar becomes a row of pills across the top of the
 * page, as in Macro: the record types, then the pages, then a "Saved views"
 * pill and a "Shared views" pill. The row scrolls sideways. The pill you are
 * on is filled. Each pill is 44px tall, a comfortable size to tap.
 *
 * The two views pills show wherever the desktop sidebar shows its Saved views
 * and Shared views sections, even before you have saved a view. Each opens a
 * sheet from the bottom holding what that section holds:
 *
 * - Saved views lists your views, or says there are none yet. With a view
 *   open, the pill reads the view's name. Where the page has a table to save,
 *   the sheet ends with "New saved view".
 * - Shared views has "Browse shared views" where the page has a table to
 *   save, and "Import a view" always.
 *
 * Those three rows close the sheet and ask the header to open the dialog. The
 * header holds the dialogs because importing a view goes to another page, and
 * this row is not on that page.
 */

const props = defineProps<{ picker: AreaPicker; canSaveView: boolean }>();
const emit = defineEmits<{ 'new-view': []; 'browse-shared': []; 'import-view': [] }>();

const route = useRoute();
const savedViewsSheetOpen = ref(false);
const sharedViewsSheetOpen = ref(false);

// This row comes and goes as you move between areas. A closed sheet would
// still leave an empty Teleport behind, so each is in the page only while it
// is open or sliding out (see useMountedWhileOpen).
const savedViewsSheet = useMountedWhileOpen(savedViewsSheetOpen);
const sharedViewsSheet = useMountedWhileOpen(sharedViewsSheetOpen);

// Tapping a view closes the sheet, and so does going anywhere else, such as
// the browser's back button.
watch(
    () => route.fullPath,
    () => {
        savedViewsSheetOpen.value = false;
        sharedViewsSheetOpen.value = false;
    },
);

// The sheet closes in the same tick as the dialog opens, as the Create menu
// hands over to its dialogs.
function openNewView() {
    savedViewsSheetOpen.value = false;
    emit('new-view');
}
function openBrowseShared() {
    sharedViewsSheetOpen.value = false;
    emit('browse-shared');
}
function openImportView() {
    sharedViewsSheetOpen.value = false;
    emit('import-view');
}

const recordAndPagePills = computed(() => [...props.picker.recordItems, ...props.picker.pageItems]);
const savedViewsPillLabel = computed(() => props.picker.matchedView?.name ?? 'Saved views');

// In a long row, such as Music's, the pill you are on can start past the
// right edge. On a phone it is the page's only title, so it is scrolled into
// view when the row appears and whenever you move to another pill.
const pillRow = useTemplateRef<HTMLElement>('pillRow');
const currentPillKey = computed(() =>
    props.picker.matchedView ? 'saved-views' : (recordAndPagePills.value.find((pill) => pill.current)?.key ?? null),
);
watch(
    currentPillKey,
    async () => {
        await nextTick();
        pillRow.value?.querySelector<HTMLElement>('[aria-current="page"]')?.scrollIntoView?.({ inline: 'nearest', block: 'nearest' });
    },
    { immediate: true },
);

const sheetClass = 'max-h-[70vh] gap-2 overflow-y-auto rounded-t-2xl pb-[calc(1rem+env(safe-area-inset-bottom,0px))]';
const sheetRowClass = 'flex h-11 items-center gap-3 rounded-lg px-3 text-sm hover:bg-muted aria-[current=page]:bg-muted';
const pillClass =
    'flex h-11 max-w-60 shrink-0 items-center gap-1 rounded-full px-4 text-sm text-muted-foreground ring-1 ring-border aria-[current=page]:bg-foreground aria-[current=page]:text-background aria-[current=page]:ring-0';
</script>

<template>
  <nav ref="pillRow" aria-label="Lists in this area" class="flex h-14 shrink-0 items-center gap-2 overflow-x-auto px-3 [scrollbar-width:none]" data-testid="mobile-pill-row">
    <router-link
      v-for="pill in recordAndPagePills"
      :key="pill.key"
      :to="pill.to"
      :aria-current="pill.current ? 'page' : undefined"
      :class="pillClass"
      data-testid="mobile-pill"
    >
      <span class="truncate">{{ pill.label }}</span>
    </router-link>
    <button
      v-if="props.picker.hasSavedViews"
      type="button"
      :aria-label="props.picker.matchedView ? `Saved view: ${props.picker.matchedView.name}` : 'Saved views'"
      :aria-current="props.picker.matchedView ? 'page' : undefined"
      :class="pillClass"
      data-testid="saved-views-pill"
      @click="savedViewsSheetOpen = true"
    >
      <span class="truncate">{{ savedViewsPillLabel }}</span>
      <ChevronDownIcon class="size-3.5 shrink-0" aria-hidden="true" />
    </button>
    <button v-if="props.picker.hasSavedViews" type="button" :class="pillClass" data-testid="shared-views-pill" @click="sharedViewsSheetOpen = true">
      <span class="truncate">Shared views</span>
      <ChevronDownIcon class="size-3.5 shrink-0" aria-hidden="true" />
    </button>
  </nav>

  <Sheet v-if="savedViewsSheet.mounted.value" v-model:open="savedViewsSheetOpen">
    <SheetContent
      side="bottom"
      :class="sheetClass"
      data-testid="saved-views-sheet"
      @close-auto-focus="savedViewsSheet.onCloseFinished"
    >
      <SheetHeader class="pb-0">
        <SheetTitle>Saved views</SheetTitle>
      </SheetHeader>
      <nav aria-label="Saved views" class="flex flex-col px-2">
        <router-link
          v-for="item in props.picker.viewItems"
          :key="item.key"
          :to="item.to"
          :aria-current="item.current ? 'page' : undefined"
          :class="sheetRowClass"
          data-testid="saved-views-sheet-item"
          @click="savedViewsSheetOpen = false"
        >
          <SavedViewIcon :appearance="item.view" class="size-4" />
          <span class="flex-1 truncate">{{ item.label }}</span>
          <CheckIcon v-if="item.current" class="size-4" aria-hidden="true" />
        </router-link>
        <p v-if="props.picker.viewItems.length === 0" class="px-3 py-2 text-sm text-muted-foreground">No saved views yet.</p>
        <button v-if="props.canSaveView" type="button" :class="sheetRowClass" data-testid="saved-views-sheet-new" @click="openNewView">
          <PlusIcon class="size-4" aria-hidden="true" />
          <span class="flex-1 truncate text-left">New saved view</span>
        </button>
      </nav>
    </SheetContent>
  </Sheet>

  <Sheet v-if="sharedViewsSheet.mounted.value" v-model:open="sharedViewsSheetOpen">
    <SheetContent
      side="bottom"
      :class="sheetClass"
      data-testid="shared-views-sheet"
      @close-auto-focus="sharedViewsSheet.onCloseFinished"
    >
      <SheetHeader class="pb-0">
        <SheetTitle>Shared views</SheetTitle>
      </SheetHeader>
      <div class="flex flex-col px-2">
        <button
          v-if="props.canSaveView"
          type="button"
          :class="sheetRowClass"
          data-testid="shared-views-sheet-browse"
          @click="openBrowseShared"
        >
          <UserGroupIcon class="size-4" aria-hidden="true" />
          <span class="flex-1 truncate text-left">Browse shared views</span>
        </button>
        <button type="button" :class="sheetRowClass" data-testid="shared-views-sheet-import" @click="openImportView">
          <ArrowDownTrayIcon class="size-4" aria-hidden="true" />
          <span class="flex-1 truncate text-left">Import a view</span>
        </button>
      </div>
    </SheetContent>
  </Sheet>
</template>
